import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../services/AuthService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

function hasStoredSessionCredentials(): boolean {
  return Boolean(localStorage.getItem('refresh_token') && localStorage.getItem('phone_number'));
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isLoggedIn: authService.getIsLoggedIn(),
    userRoles: authService.getRoles(),
  });
  // If the session was already synced (e.g. OTP → complete-profile → onboarding),
  // render immediately — don't blank the screen waiting on initialize().
  const [isReady, setIsReady] = useState(() => authService.getIsLoggedIn());

  useEffect(() => {
    let cancelled = false;

    const syncAuth = async () => {
      const alreadyLoggedIn = authService.getIsLoggedIn();
      if (alreadyLoggedIn) {
        if (!cancelled) {
          setAuthState({
            isLoggedIn: true,
            userRoles: authService.getRoles(),
          });
          setIsReady(true);
        }
        return;
      }

      // Tokens in localStorage but AuthService not synced yet (OTP → SPA navigate race).
      if (hasStoredSessionCredentials()) {
        authService.syncSessionFromStorage({ scheduleRefresh: true });
        if (authService.getIsLoggedIn()) {
          if (!cancelled) {
            setAuthState({
              isLoggedIn: true,
              userRoles: authService.getRoles(),
            });
            setIsReady(true);
          }
          return;
        }
      }

      try {
        // Hard reload / deep link: restore session from localStorage before redirecting.
        await authService.initialize();
      } catch (error) {
        console.error('ProtectedRoute auth initialization failed:', error);
      } finally {
        if (!cancelled) {
          setAuthState({
            isLoggedIn: authService.getIsLoggedIn(),
            userRoles: authService.getRoles(),
          });
          setIsReady(true);
        }
      }
    };

    void syncAuth();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  let { isLoggedIn, userRoles } = authState;

  if (!isReady) {
    return null;
  }

  // Last chance: storage may have been written a tick after mount (OTP handoff).
  if (!isLoggedIn && hasStoredSessionCredentials() && authService.syncSessionFromStorage()) {
    isLoggedIn = true;
    userRoles = authService.getRoles();
  }

  // If not logged in, redirect to login and preserve the destination for post-auth return
  if (!isLoggedIn) {
    const redirectUrl = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to="/login"
        replace
        state={{ redirectUrl }}
      />
    );
  }

  // If roles are required, check if user has the required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      // Only redirect once roles are known (non-empty). Empty roles may still be loading.
      if (userRoles.length > 0) {
        const defaultUrl = userRoles.includes('HOST')
          ? '/host/requests'
          : userRoles.includes('TUTOR')
            ? '/tutor/listing'
            : '/user/search';
        return <Navigate to={defaultUrl} replace />;
      }
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
